import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { IndexPath } from '@/Config/app/router/index.path';
import { UrlInput } from '@/Domain/components/KnowledgeBase/UploadUrlModal/components/UrlInput';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { useShopifyMcpActionForm } from '@/Domain/hooks/Action/ShopifyMcp/useShopifyMcpActionForm.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const ShopifyConnectModal = () => {
  const { t } = useTranslation(['knowledge_base', 'shopify_mcp']);
  const { isOpen, closeModal } = useModalStore();
  const { form, handleSubmit } = useShopifyMcpActionForm();
  const navigate = useNavigate();
  const { scenario } = useParams<{ scenario: string }>();

  const isModalOpen = isOpen(MODAL_NAMES.SHOPIFY_CONNECT_MODAL_ID);

  const handleNavigateToActions = () => {
    closeModal();
    if (scenario) {
      navigate(`/i/${scenario}/${IndexPath.actions}`);
      return;
    }
    navigate(IndexPath.actions);
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={closeModal}>
      <DialogContent className="w-lg">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} autoComplete="off" className="space-y-5">
            <div className="flex flex-col">
              <DialogHeader>
                <DialogTitle>{t('knowledge_base:modals.external_apps.shopify.title')}</DialogTitle>
              </DialogHeader>
              <DialogDescription asChild>
                <div className="mt-2">
                  <p>
                    {t('knowledge_base:modals.external_apps.shopify.instructions_prefix')}{' '}
                    <a
                      href="/#"
                      className="font-semibold text-foreground underline hover:!text-muted-foreground"
                      onClick={(event) => {
                        event.preventDefault();
                        handleNavigateToActions();
                      }}
                    >
                      {t('knowledge_base:modals.external_apps.shopify.actions_link')}
                    </a>{' '}
                    {t('knowledge_base:modals.external_apps.shopify.instructions_suffix')}
                  </p>

                  <div className="mt-4 space-y-3">
                    <span className="mb-2">
                      {t('knowledge_base:modals.external_apps.shopify.alternatively')}
                    </span>
                    <Alert variant="warning">
                      <AlertDescription>
                        {t('knowledge_base:modals.external_apps.shopify.important_note')}
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              </DialogDescription>
            </div>

            <FormField
              name="shopify_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shopify_mcp:form.shopify_url_label')}</FormLabel>
                  <FormControl>
                    <div data-testid="kb-shopify-mcp-action-form-url">
                      <UrlInput type={WebsiteModalTypeEnum.SINGLE} {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator className="w-full my-6" />

            <DialogFooter className="flex flex-row !justify-between !items-center mt-1">
              <Button variant="secondary" type="button" onClick={closeModal}>
                {t('shopify_mcp:button.cancel')}
              </Button>
              <Button
                variant="dark"
                type="submit"
                data-testid="kb-shopify-mcp-action-sheet-submit-button"
              >
                {t('shopify_mcp:button.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
